import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Eye, Database, Mail } from "lucide-react";
import { BRAND, LOGO_URL } from "../common/constants";
import Footer from "../common/Footer";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND.parent} className="h-10 w-auto rounded-md" />
            <span className="font-heading font-bold text-[#071734]">{BRAND.parent}</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#00a3e0]">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="container-main max-w-4xl">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#00a3e0] rounded-lg flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-[#071734]">Privacy Policy</h1>
                <p className="text-gray-500">Last Updated: August 26, 2026</p>
              </div>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Introduction */}
            <section className="p-6 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-700">
                {BRAND.parent} ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, and safeguard your information when you use our Services.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-[#00a3e0]" /> Information We Collect
              </h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>Information You Provide:</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name and contact information (email, phone number)</li>
                  <li>Resume content and work history you upload or enter</li>
                  <li>Job descriptions you provide for optimization</li>
                  <li>Payment information (processed securely by Stripe)</li>
                </ul>
                <p className="mt-4"><strong>Automatically Collected Information:</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device and browser information</li>
                  <li>IP address and general location</li>
                  <li>Usage data and interaction with our Services</li>
                </ul>
              </div>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#00a3e0]" /> How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>To provide and improve our Services</li>
                <li>To generate AI-optimized documents based on your input</li>
                <li>To process payments and fulfill orders</li>
                <li>To communicate with you about your order or support requests</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Data Retention</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  <strong>Temporary Processing and Storage:</strong> Resume content, personal information submitted for document
                  generation, and generated career documents are temporarily stored for up to approximately 24 hours so we can
                  process your request and allow you to download your files. This temporary data is scheduled for automatic deletion
                  after the retention period.
                </p>
                <p>
                  <strong>Payment Records:</strong> We retain transaction records as required for accounting and legal purposes.
                </p>
                <p>
                  <strong>Support Communications:</strong> If you contact us for support, we may retain those communications 
                  to provide better service.
                </p>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Third-Party Services</h2>
              <div className="space-y-4 text-gray-700">
                <p>We use the following third-party services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Stripe:</strong> For secure payment processing</li>
                  <li><strong>Anthropic (Claude AI):</strong> For AI-powered document generation</li>
                </ul>
                <p className="mt-4 text-sm">
                  These services have their own privacy policies governing how they handle your data.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Your Rights</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Request information about what data we have about you</li>
                <li>Request deletion of your data</li>
                <li>Opt out of marketing communications</li>
                <li>Contact us with privacy concerns</li>
              </ul>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Security</h2>
              <p className="text-gray-700">
                We implement appropriate technical and organizational measures to protect your information. 
                However, no method of transmission over the Internet is 100% secure. While we strive to 
                protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Contact */}
            <section className="p-6 bg-[#00a3e0]/10 rounded-lg border border-[#00a3e0]/30">
              <h2 className="text-xl font-semibold text-[#071734] mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#00a3e0]" /> Contact Us
              </h2>
              <p className="text-gray-700">
                For privacy-related questions or to exercise your rights, please contact us at:
              </p>
              <p className="mt-2">
                <a href="mailto:aaron@aiquires.com" className="text-[#00a3e0] hover:underline font-medium">
                  aaron@aiquires.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer variant="simple" />
    </div>
  );
};

export default PrivacyPage;
